// src/components/SlideAnalysis.jsx - Improved Layout with Lazy Thumbnail Loading
import React, { useState, useEffect } from 'react';
import {
  Layers, ChevronLeft, ChevronRight, Brain, RefreshCw,
  CheckCircle, Loader2, Sparkles, Eye
} from 'lucide-react';
import ImageGenerator from './ImageGenerator';
import LazyThumbnail from './LazyThumbnail';
import { DuarteSlideCard } from './DuarteSlideCard';
import DesignQualityPanel from './DesignQualityPanel';
import { withBackend } from '../utils/api';
import { sampleSlides, sampleAnalyses } from '../data/sampleSlideData';

const SlideAnalysis = ({
  analysis,
  apiKey,
  uploadedFileName,
  initialDemoMode = false,
  projectId, // NEW: Pass project ID for lazy thumbnail caching
  fileHash, // NEW: File hash for on-demand thumbnail generation
  isLargeFile = false // NEW: Flag for large files (>20MB)
}) => {
  const [slidesData, setSlidesData] = useState([]);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [slideAnalyses, setSlideAnalyses] = useState({});
  const [analyzing, setAnalyzing] = useState(false);
  const [demoMode, setDemoMode] = useState(initialDemoMode);

  // Initialize slides data when analysis changes or demo mode toggles
  useEffect(() => {
    if (demoMode) {
      // Load sample data in demo mode
      setSlidesData(sampleSlides);
      setSlideAnalyses(sampleAnalyses);
      setCurrentSlideIndex(0);
    } else if (analysis?.slides) {
      console.log('SlideAnalysis: Received slides data:', analysis.slides);
      console.log('SlideAnalysis: First slide thumbnail_url:', analysis.slides[0]?.thumbnail_url);
      console.log('SlideAnalysis: First slide design_quality:', analysis.slides[0]?.design_quality);
      console.log('SlideAnalysis: First slide keys:', Object.keys(analysis.slides[0] || {}));
      setSlidesData(analysis.slides);
      setCurrentSlideIndex(0);
      setSlideAnalyses({});
    } else {
      console.log('SlideAnalysis: No slides data found in analysis');
      setSlidesData([]);
      setCurrentSlideIndex(0);
      setSlideAnalyses({});
    }
  }, [analysis, demoMode]);

  const toggleDemoMode = () => {
    setDemoMode(!demoMode);
  };

  const selectSlide = (index) => {
    setCurrentSlideIndex(index);
  };

  const previousSlide = () => {
    if (currentSlideIndex > 0) {
      setCurrentSlideIndex(currentSlideIndex - 1);
    }
  };

  const nextSlide = () => {
    if (currentSlideIndex < slidesData.length - 1) {
      setCurrentSlideIndex(currentSlideIndex + 1);
    }
  };

  const analyzeCurrentSlide = async () => {
    if (!apiKey) {
      alert('The design assistant is not currently available.');
      return;
    }

    const slideData = slidesData[currentSlideIndex];
    if (!slideData) {
      console.warn('No slide data available at index', currentSlideIndex);
      return;
    }

    setAnalyzing(true);

    try {
      const presentationContext = {
        totalSlides: slidesData.length,
        slidePosition: currentSlideIndex + 1,
        presentationMetadata: analysis?.metadata || {},
        companyContext: analysis?.companyContext || analysis?.company_context || '',
      };

      const audienceInfo = analysis?.audienceInfo || analysis?.audience_info || null;

      const response = await fetch(withBackend('/analyze-slide'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          api_key: apiKey,
          slide_data: slideData,
          presentation_context: presentationContext,
          audience_info: audienceInfo,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Slide analysis failed (status ${response.status})`);
      }

      const result = await response.json();

      setSlideAnalyses((prev) => ({
        ...prev,
        [currentSlideIndex]: result,
      }));

    } catch (error) {
      console.error('Slide analysis error:', error);
      alert('Failed to analyze slide: ' + error.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const getTotalCount = () => slidesData.length;
  const getAnalyzedCount = () => Object.keys(slideAnalyses).length;

  // Check if we have no slides data at all
  if (slidesData.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Layers className="w-8 h-8 text-purple-600" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Slides Available</h3>
        <p className="text-gray-600 mb-6">
          {demoMode ? 'Loading demo slides...' : 'Upload a presentation first to analyze individual slides'}
        </p>
        {!demoMode && (
          <button
            onClick={toggleDemoMode}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 mx-auto"
          >
            <Eye className="w-5 h-5" />
            View Demo Analysis
          </button>
        )}
      </div>
    );
  }

  const currentSlide = slidesData[currentSlideIndex];
  const currentAnalysis = slideAnalyses[currentSlideIndex];

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="flex items-center justify-center gap-4 mb-4">
          <h2 className="text-3xl font-bold text-gray-900">Slide-by-Slide Analysis</h2>
          {(analysis?.slides || demoMode) && (
            <button
              onClick={toggleDemoMode}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm ${
                demoMode
                  ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                  : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
              }`}
            >
              {demoMode ? (
                <>
                  <Layers className="w-4 h-4" />
                  Exit Demo
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4" />
                  View Demo
                </>
              )}
            </button>
          )}
        </div>
        <p className="text-lg text-gray-600">
          {demoMode
            ? 'Demo Mode - Exploring sample presentation analysis'
            : 'Analyze each slide individually for detailed design recommendations and visual suggestions'}
        </p>
      </div>

      {/* Slides Overview */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-900">Presentation Slides</h3>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Progress:</span>
            <span className="text-sm font-medium text-blue-600">
              {getAnalyzedCount()}/{getTotalCount()} analyzed
            </span>
          </div>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          {slidesData.map((slide, index) => (
            <div
              key={index}
              onClick={() => selectSlide(index)}
              className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 hover:shadow-md ${
                index === currentSlideIndex 
                  ? 'border-blue-500 bg-blue-50 shadow-md' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="aspect-video bg-gray-100 rounded mb-3 overflow-hidden">
                <LazyThumbnail
                  thumbnailUrl={slide.thumbnail_url}
                  lazyThumbnailUrl={slide.lazy_thumbnail_url}
                  slideNumber={index + 1}
                  className="w-full h-full object-contain rounded"
                  onError={(err) => console.error(`Thumbnail error for slide ${index + 1}:`, err)}
                  slide={slide}
                  projectId={projectId}
                  fileHash={fileHash}
                  isLargeFile={isLargeFile}
                />
              </div>
              <div className="text-center">
                <div className="font-medium text-sm mb-1">Slide {index + 1}</div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">{slide.shapes_count} shapes</span>
                  {slideAnalyses[index] && (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Analysis Area - Improved 3-Column Layout */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        
        {/* Left Sidebar - Slide Preview & Info (3 columns) */}
        <div className="xl:col-span-3">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            {/* Header */}
            <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 border-b">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900">
                    Slide {currentSlideIndex + 1}
                  </h3>
                  <p className="text-sm text-gray-600">{currentSlide.layout_type}</p>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={previousSlide}
                    disabled={currentSlideIndex === 0}
                    className="p-1.5 rounded-full bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={nextSlide}
                    disabled={currentSlideIndex === slidesData.length - 1}
                    className="p-1.5 rounded-full bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            <div className="p-4">
              {/* Thumbnail */}
              <div className="aspect-video bg-gray-100 rounded-lg border border-gray-200 mb-4 overflow-hidden">
                <LazyThumbnail
                  thumbnailUrl={currentSlide.thumbnail_url}
                  lazyThumbnailUrl={currentSlide.lazy_thumbnail_url}
                  slideNumber={currentSlideIndex + 1}
                  className="w-full h-full object-contain"
                  onError={(err) => console.error(`Large thumbnail error for slide ${currentSlideIndex + 1}:`, err)}
                  slide={currentSlide}
                  projectId={projectId}
                  fileHash={fileHash}
                  isLargeFile={isLargeFile}
                />
              </div>

              {/* Quick Stats */}
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-gray-50 rounded-lg p-2 text-center">
                    <div className="text-lg font-semibold text-gray-900">{currentSlide.shapes_count}</div>
                    <div className="text-xs text-gray-600">Shapes</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2 text-center">
                    <div className="text-lg font-semibold text-gray-900">{currentSlide.text_boxes}</div>
                    <div className="text-xs text-gray-600">Text Boxes</div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Density:</span>
                  <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                    currentSlide.visual_density === 'visual-heavy' ? 'bg-red-100 text-red-800' :
                    currentSlide.visual_density === 'balanced' ? 'bg-green-100 text-green-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {currentSlide.visual_density}
                  </span>
                </div>

                {/* Visual Elements */}
                {(currentSlide.images > 0 || currentSlide.charts > 0 || currentSlide.tables > 0) && (
                  <div className="flex flex-wrap gap-1">
                    {currentSlide.images > 0 && (
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                        Images: {currentSlide.images}
                      </span>
                    )}
                    {currentSlide.charts > 0 && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                        Charts: {currentSlide.charts}
                      </span>
                    )}
                    {currentSlide.tables > 0 && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                        Tables: {currentSlide.tables}
                      </span>
                    )}
                  </div>
                )}

                {/* Content Preview */}
                {currentSlide.text_content && (
                  <div>
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Content Preview</h5>
                    <div className="text-xs text-gray-600 bg-gray-50 rounded p-2 max-h-20 overflow-y-auto">
                      {currentSlide.text_content.substring(0, 120)}
                      {currentSlide.text_content.length > 120 && '...'}
                    </div>
                  </div>
                )}

                {/* Analyze Button */}
                {!demoMode && (
                  <button
                    onClick={analyzeCurrentSlide}
                    disabled={analyzing || !apiKey}
                    className={`w-full px-4 py-2 rounded-md transition-colors flex items-center justify-center gap-2 ${
                      currentAnalysis
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {analyzing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : currentAnalysis ? (
                      <RefreshCw className="w-4 h-4" />
                    ) : (
                      <Brain className="w-4 h-4" />
                    )}
                    {analyzing ? 'Analyzing...' : currentAnalysis ? 'Re-analyze' : 'Analyze Slide'}
                  </button>
                )}
                {demoMode && currentAnalysis && (
                  <div className="w-full px-4 py-2 rounded-md bg-purple-100 text-purple-700 text-center text-sm font-medium">
                    ✓ Sample Analysis Loaded
                  </div>
                )}
              </div>
            </div>

            {/* Design Quality Panel */}
            {currentSlide.design_quality ? (
              <div className="mt-4">
                <DesignQualityPanel designQuality={currentSlide.design_quality} />
              </div>
            ) : (
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                <strong>Debug:</strong> No design_quality data found for slide {currentSlideIndex + 1}
                <br />
                Available keys: {Object.keys(currentSlide || {}).join(', ')}
              </div>
            )}
          </div>
        </div>

        {/* Main Content - Analysis Results (9 columns) */}
        <div className="xl:col-span-9">
          {analyzing ? (
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <div className="text-center py-12">
                <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin text-blue-600" />
                <p className="text-gray-600">Analyzing slide...</p>
              </div>
            </div>
          ) : currentAnalysis ? (
            <div className="space-y-6">
              <DuarteSlideCard
                slide={{
                  ...currentSlide,
                  recommendations: currentSlide.recommendations || currentAnalysis
                }}
                index={currentSlideIndex}
              />
              {/* Keep Image Generator if visual suggestions are available */}
              {currentAnalysis.visualSuggestions?.needsVisual && currentAnalysis.visualSuggestions.recommendedImage && (
                <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                    <h4 className="font-semibold text-gray-900">AI Visual Suggestion</h4>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      currentAnalysis.visualSuggestions.recommendedImage.priority === 'high' ? 'bg-red-100 text-red-800' :
                      currentAnalysis.visualSuggestions.recommendedImage.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {currentAnalysis.visualSuggestions.recommendedImage.priority} priority
                    </span>
                  </div>
                  <ImageGenerator
                    imagesuggestion={currentAnalysis.visualSuggestions.recommendedImage}
                    apiKey={apiKey}
                    visualSuggestionDescription={currentAnalysis.visualSuggestions.recommendedImage.description}
                    onImageGenerated={(result) => {
                      console.log('Slide image generated:', result);
                    }}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <div className="text-center py-12">
                <Brain className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <h4 className="text-lg font-medium text-gray-900 mb-2">Ready to Analyze</h4>
                <p className="text-gray-600 mb-4">Click "Analyze Slide" to get detailed recommendations and visual suggestions for this slide</p>
                {!apiKey && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 max-w-md mx-auto">
                    <p className="text-sm text-yellow-800">
                      Please enter your OpenAI API key in the upload tab to enable analysis.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SlideAnalysis;

